import type { CustomAdjustmentTemplateBox } from '@lite-workspace/dto-fotoclass-templates';
import { BoxType } from '@lite-workspace/dto-fotoclass-templates';
import type { DrawableAdjustmentData } from '../../interfaces/drawable-data.interface';
import { buildBoxPassthroughMeta, carriedBoxFields, mergeBoxPassthroughMeta } from '../passthrough';
import { buildDrawableEffects, mergeDrawableEffects } from './effects';
import { rectFromBox, rectToBox } from './rect';
import { mergeRotation } from './rotation';
import type { BoxDecoder, BoxEncoder } from './codec-contract';

export const decode: BoxDecoder<CustomAdjustmentTemplateBox, DrawableAdjustmentData> = (box, ctx) => {
  const templateMeta = buildBoxPassthroughMeta(box);
  const effects = buildDrawableEffects(box.effects);
  return {
    id: box.id,
    type: 'adjustment',
    ...carriedBoxFields(box),
    order: 0,
    canResize: true,
    params: box.params,
    rotation: box.rotation,
    naturalAligns: { container: rectFromBox(box.rect, ctx) },
    ...(box.opacity !== undefined && { opacity: box.opacity }),
    ...(box.blendMode !== undefined && { blendMode: box.blendMode }),
    ...(box.visible !== undefined && { visible: box.visible }),
    ...(box.locked !== undefined && { locked: box.locked }),
    ...(effects !== undefined && { effects }),
    ...(templateMeta !== undefined && { templateMeta }),
  };
};

export const encode: BoxEncoder<DrawableAdjustmentData, CustomAdjustmentTemplateBox> = (drawable, ctx) => {
  const box: CustomAdjustmentTemplateBox = {
    type: BoxType.ADJUSTMENT,
    id: drawable.id,
    ...carriedBoxFields(drawable),
    rect: rectToBox(drawable.naturalAligns.container, ctx),
    rotation: mergeRotation(drawable, ctx),
    params: drawable.params,
    ...(drawable.opacity !== undefined && { opacity: drawable.opacity }),
    ...(drawable.blendMode !== undefined && { blendMode: drawable.blendMode }),
    ...(drawable.visible !== undefined && { visible: drawable.visible }),
    ...(drawable.locked !== undefined && { locked: drawable.locked }),
    effects: mergeDrawableEffects(
      drawable.effects,
      drawable.templateMeta?.rawEffects,
      drawable.layerStyleRendering === 'live' ? 'typed' : undefined,
    ),
  };
  return mergeBoxPassthroughMeta(box, drawable.templateMeta);
};
